import { useEffect, useRef, useState } from 'react'
import { AlertOctagon, Loader2, RotateCcw, X } from 'lucide-react'
import { CC, EYEBROW } from '../cc/tokens'
import { ago, clock } from '../lib/format'
import {
  actOnStarBreakdown, openStarBreakdown, type StarBreakdown, type StarBreakdownCode,
} from './useStarApi'

/** STAR's side of MbmtBreakdownForm.tsx -- same shape (category, severity, ETA, contact,
 * note), same site_breakdown backend, same two-minute-form reasoning: a form nobody fills
 * in is worse than none. Ported rather than shared, same call MbmtBreakdownForm.tsx
 * itself already made for Ultratech's BreakdownForm: each site's form rides its own
 * backend module with its own field names and action verbs, even where the shape lines
 * up (here, site_breakdown on both sides -- STAR still gets its own copy because the
 * position-resolution source differs: star_vehicle_telemetry, not an in-memory dict). */

export const SEVERITIES = [
  { id: 'minor', label: 'Minor', hint: 'can continue, fix at depot' },
  { id: 'major', label: 'Major', hint: 'must return, route re-planned' },
  { id: 'immobilised', label: 'Immobilised', hint: 'stranded, recovery needed' },
] as const

export function StarBreakdownForm({
  vehicleId, existing, codes, onClose, onSaved,
}: {
  vehicleId: string
  existing: StarBreakdown | null
  codes: StarBreakdownCode[]
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const [category, setCategory] = useState(existing?.category ?? codes[0]?.code ?? 'OTHER')
  const [severity, setSeverity] = useState<string>(existing?.severity ?? 'major')
  const [eta, setEta] = useState(existing?.eta_min != null ? String(existing.eta_min) : '')
  const [contact, setContact] = useState(existing?.contact ?? '')
  const [note, setNote] = useState(existing?.note ?? '')
  const [busy, setBusy] = useState<'save' | 'resolve' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const first = useRef<HTMLButtonElement>(null)

  useEffect(() => { first.current?.focus() }, [])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  async function save(): Promise<void> {
    setBusy('save')
    setError(null)
    try {
      await openStarBreakdown({
        vehicle_id: vehicleId,
        category, severity,
        note: note.trim() || undefined,
        contact: contact.trim() || undefined,
        eta_min: eta.trim() === '' ? null : Number(eta),
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the server')
    } finally {
      setBusy(null)
    }
  }

  async function resolve(): Promise<void> {
    if (existing == null) return
    setBusy('resolve')
    setError(null)
    try {
      await actOnStarBreakdown(existing.breakdown_id, 'resolve', { note: note.trim() || undefined })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the server')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.28)] p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={`Breakdown for ${vehicleId}`}
           className="w-full max-w-[420px] animate-slide-up rounded-xl border border-[rgba(15,23,42,0.10)]
                      bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.55)]">
        <header className="flex items-center gap-2.5 border-b border-[rgba(15,23,42,0.07)] px-4 py-3">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-[rgba(244,63,94,0.10)]">
            <AlertOctagon size={15} className="text-[#9F1239]" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[13px] font-semibold text-[#0F172A]">{vehicleId}</div>
            <div className={EYEBROW}>{existing && existing.cleared_ts == null ? 'update breakdown' : 'mark breakdown'}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="ml-auto rounded p-1 text-[#8794A7] hover:bg-[rgba(15,23,42,0.05)]">
            <X size={15} />
          </button>
        </header>

        {existing && existing.cleared_ts == null && existing.opened_ts != null && (
          <div className="flex items-center justify-between gap-2 border-b border-[rgba(15,23,42,0.07)]
                          bg-[rgba(15,23,42,0.02)] px-4 py-1.5 text-[11px] text-[#8794A7]">
            <span>reported {ago(existing.opened_ts)} ago · {clock(existing.opened_ts)}</span>
          </div>
        )}

        <div className="grid max-h-[70vh] gap-3.5 overflow-y-auto px-4 py-4">
          <Field label="Fault">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2.5 py-2
                               text-[13px] text-[#0F172A]">
              {(codes.length ? codes : [{ code: 'OTHER', name: 'Other' }]).map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Severity">
            <div className="grid grid-cols-3 gap-1.5">
              {SEVERITIES.map((s, i) => (
                <button
                  key={s.id}
                  ref={i === 0 ? first : undefined}
                  type="button"
                  onClick={() => setSeverity(s.id)}
                  title={s.hint}
                  className={`rounded-lg border px-2 py-2 text-left transition-colors ${
                    severity === s.id
                      ? 'border-[rgba(244,63,94,0.35)] bg-[rgba(244,63,94,0.10)]'
                      : 'border-[rgba(15,23,42,0.10)] bg-white hover:bg-[rgba(15,23,42,0.03)]'
                  }`}
                >
                  <span className={`block text-[12px] font-medium ${
                    severity === s.id ? 'text-[#9F1239]' : 'text-[#0F172A]'}`}>{s.label}</span>
                  <span className="mt-0.5 block text-[10px] leading-tight text-[#8794A7]">
                    {s.hint}
                  </span>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Back in service (min)">
              <input value={eta} onChange={(e) => setEta(e.target.value.replace(/[^\d]/g, ''))}
                     inputMode="numeric" placeholder="unknown"
                     className="w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white
                                px-2.5 py-2 font-mono text-[13px] text-[#0F172A]" />
            </Field>
            <Field label="Contact">
              <input value={contact} onChange={(e) => setContact(e.target.value)}
                     placeholder="who was spoken to"
                     className="w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white
                                px-2.5 py-2 font-mono text-[13px] text-[#0F172A]" />
            </Field>
          </div>

          <Field label="Note">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      placeholder="where it stopped, what was tried, who is attending"
                      className="w-full resize-none rounded-lg border border-[rgba(15,23,42,0.12)]
                                 bg-white px-2.5 py-2 text-[13px] text-[#0F172A]
                                 placeholder:text-[#B3BECE]" />
          </Field>

          {error && (
            <p className="rounded-lg border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.10)]
                          px-2.5 py-2 text-[12px] text-[#9F1239]">
              Not saved — {error}. The vehicle is unchanged.
            </p>
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-[rgba(15,23,42,0.07)] px-4 py-3">
          {existing && existing.cleared_ts == null && (
            <button type="button" onClick={() => void resolve()} disabled={busy !== null}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.12)]
                               px-2.5 py-2 text-[12px] text-[#047857] hover:bg-[rgba(16,185,129,0.08)]
                               disabled:opacity-50">
              {busy === 'resolve' ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              Back in service
            </button>
          )}
          <span className="flex-1" />
          <button type="button" onClick={onClose}
                  className="rounded-lg px-3 py-2 text-[12px] text-[#475569] hover:bg-[rgba(15,23,42,0.05)]">
            Cancel
          </button>
          <button type="button" onClick={() => void save()} disabled={busy !== null}
                  style={{ background: CC.ink }}
                  className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-medium
                             text-white transition-transform active:scale-[0.97] disabled:opacity-50">
            {busy === 'save' && <Loader2 size={13} className="animate-spin" />}
            {existing && existing.cleared_ts == null ? 'Update' : 'Mark down'}
          </button>
        </footer>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="grid gap-1.5">
      <span className={EYEBROW}>{label}</span>
      {children}
    </label>
  )
}
